import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import PgIcon from './pg-icon';
import PgAvatar from './pg-avatar';
import PgNavMenuItems from './pg-nav-menu-items';
import PgSidebarBusinessMenu from './pg-sidebar-business-menu';
import PgFloatingPanel from './pg-floating-panel';

/**
 * The sidebar's bottom zone — `.pg-sidebar-bot`. Renders an optional list
 * of static bottom nav rows (e.g. Settings / Report a Bug / Help &
 * Support, pinned above the fold — pass `items`), then the user row that
 * opens the `PgSidebarBusinessMenu` dropdown. Pegment equivalent of this
 * app's legacy `BottomNav` (`app/components/layout/bottom-nav.js`), which
 * is `BottomMenuItems` (static rows) + `ProfileCard` (the business-switcher
 * trigger) — same two pieces, bundled here since neither is meaningful on
 * its own within the sidebar's bottom zone.
 */
const PgNavBottom = ({
    user,
    businesses: businessesProp,
    settingsHref,
    onBusinessChange,
    onDropdownAction,
    onHelpAction,
    sidebarRef,
    className,
}) => {
    const userRowRef = useRef(null);
    const helpTriggerRef = useRef(null);
    const mainPanelRef = useRef(null);
    const subPanelRef = useRef(null);

    const [businesses, setBusinesses] = useState(businessesProp);
    const [openMenu, setOpenMenu] = useState('none'); // 'none' | 'main'
    const [helpOpen, setHelpOpen] = useState(false);

    useEffect(() => setBusinesses(businessesProp), [businessesProp]);

    const closeAll = useCallback(() => {
        setOpenMenu('none');
        setHelpOpen(false);
    }, []);

    // Close on outside click / Escape while the dropdown is open. A click
    // anywhere inside the sidebar itself (nav items, toggle button, ...)
    // does not count as "outside" — only the sidebar `ref` passed in via
    // `sidebarRef`, and the floating panels themselves, are excluded.
    useEffect(() => {
        if (openMenu !== 'main') return undefined;

        const handlePointerDown = (e) => {
            const inSidebar = sidebarRef?.current?.contains(e.target);
            const inMain = mainPanelRef.current?.contains(e.target);
            const inSub = subPanelRef.current?.contains(e.target);
            if (!inSidebar && !inMain && !inSub) closeAll();
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') closeAll();
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKey);
        };
    }, [openMenu, closeAll, sidebarRef]);

    const activeBusiness = businesses.find((b) => b.active) || businesses[0];

    const handleBusinessSelect = (id) => {
        setBusinesses((prev) => prev.map((b) => ({ ...b, active: b.id === id })));
        closeAll();
        onBusinessChange?.(id);
    };

    const handleDropdownAction = (type) => {
        closeAll();
        onDropdownAction?.(type);
    };

    const handleHelpAction = (item) => {
        closeAll();
        onHelpAction?.(item);
    };

    const handleHelpTriggerClick = (e) => {
        e.stopPropagation();
        setHelpOpen(true);
    };

    return (
        <div className={['pg-sidebar-bot', className].filter(Boolean).join(' ')}>
            <div
                ref={userRowRef}
                className={[
                    'pg-user-row',
                    'is-collapsed',
                    openMenu === 'main' && 'is-active popup-open',
                ].filter(Boolean).join(' ')}
                role="button"
                tabIndex={0}
                onClick={() => setOpenMenu((m) => (m === 'main' ? 'none' : 'main'))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenMenu((m) => (m === 'main' ? 'none' : 'main'));
                    }
                }}
            >
                <PgAvatar src={user.avatar} email={user.email} name={user.name} size="md" />
                <div className="pg-user-details">
                    <div className="pg-user-name">{user.name}</div>
                    <div className="pg-user-company">{user.company || activeBusiness?.name || ''}</div>
                </div>
                <span className="pg-user-expand">
                    <PgIcon name="selector" size={16} />
                </span>
            </div>

            {openMenu === 'main' && (
                <PgFloatingPanel ref={mainPanelRef} anchorRef={userRowRef} placement="above" width={250}>
                    <PgSidebarBusinessMenu
                        businesses={businesses}
                        onSelect={handleBusinessSelect}
                        onAdd={() => handleDropdownAction('add')}
                        settingsHref={settingsHref}
                        onSettingsClick={() => handleDropdownAction('settings')}
                        helpTriggerRef={helpTriggerRef}
                        onHelpTriggerClick={handleHelpTriggerClick}
                        onLogout={() => handleDropdownAction('logout')}
                    />
                </PgFloatingPanel>
            )}

            {openMenu === 'main' && helpOpen && (
                <PgFloatingPanel ref={subPanelRef} anchorRef={helpTriggerRef} placement="right" width={220}>
                    <div className="pg-dropdown-content">
                        <div className="pg-dropdown-action" onClick={() => handleHelpAction('help-center')}>
                            <PgIcon name="lifebuoy" size={14} /> Help Center
                        </div>
                        <div className="pg-dropdown-action" onClick={() => handleHelpAction('report-bug')}>
                            <PgIcon name="bug" size={14} /> Report a Bug
                        </div>
                    </div>
                </PgFloatingPanel>
            )}
        </div>
    );
};

PgNavBottom.propTypes = {
    /** Optional static rows pinned above the user row, e.g. Settings / Report a Bug / Help & Support. Rendered via `PgNavMenuItems`. */
    items: PropTypes.array,
    activeId: PropTypes.string,
    onItemClick: PropTypes.func,
    /** `avatar` (direct image URL) takes priority over `email`'s Gravatar lookup. */
    user: PropTypes.shape({
        name: PropTypes.string,
        email: PropTypes.string,
        avatar: PropTypes.string,
        company: PropTypes.string,
    }),
    /** `[{ id, name, plan, active }]` — rendered by `PgSidebarBusinessMenu`/`PgBusinessItem` in the dropdown. */
    businesses: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        plan: PropTypes.string,
        active: PropTypes.bool,
    })),
    settingsHref: PropTypes.string,
    onBusinessChange: PropTypes.func,
    onDropdownAction: PropTypes.func,
    onHelpAction: PropTypes.func,
    /** Ref to the whole sidebar element — clicks inside it don't count as "outside" for the dropdown's close-on-outside-click behavior. Pass the same ref your `PgSidebar` (or equivalent) root uses. */
    sidebarRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any }),
    ]),
    className: PropTypes.string,
};

PgNavBottom.defaultProps = {
    items: [],
    activeId: undefined,
    onItemClick: undefined,
    user: {},
    businesses: [],
    settingsHref: undefined,
    onBusinessChange: undefined,
    onDropdownAction: undefined,
    onHelpAction: undefined,
    sidebarRef: undefined,
    className: '',
};

export default PgNavBottom;
